const STANDARD_USER_WEB_INTENDED_USE_STATEMENT =
  "The SaMD is a platform for professionals to collect health data and support evaluation of biomarkers (HR, RR, TV, FEV1, FVC, PEF, FEV1/FVC) via validated modules, without performing diagnosis or treatment.";
const CLINICAL_USER_WEB_INTENDED_USE_STATEMENT =
  "The purpleDx platform is intended for healthcare professionals to collect health data and support evaluation of biomarkers (HR, RR, TV, FEV1, FVC, PEF, FEV1/FVC) through validated modules. It does not independently provide a diagnosis, prescribe treatment, or replace professional clinical judgment.";
const RESEARCH_USER_WEB_INTENDED_USE_STATEMENT =
  "The platform is intended for research purposes only. It enables researchers and professionals to collect health-related data and support the evaluation of biomarkers, including HR, RR, TV, FEV1, FVC, PEF, and FEV1/FVC, using validated modules. The platform is not intended for clinical use and does not perform or support diagnosis, treatment, or clinical decision-making. It is not currently intended to be used as a Software as a Medical Device (SaMD).";

export const PRODUCT_PRESENTATION_PROFILE_LABELLING_FALLBACKS = Object.freeze({
  "uk-ce-class-iia": Object.freeze({
    branding: Object.freeze({
      appName: "pDx",
      logoAsset: "pdx_logo.png",
    }),
    labelling: Object.freeze({
      exclusivelyForClinicalInvestigations: true,
      userWebApp: Object.freeze({
        intendedUseStatement: STANDARD_USER_WEB_INTENDED_USE_STATEMENT,
      }),
    }),
  }),
  "eu-ce-class-iia": Object.freeze({
    branding: Object.freeze({
      appName: "pDx",
      logoAsset: "pdx_logo.png",
    }),
    labelling: Object.freeze({
      exclusivelyForClinicalInvestigations: true,
      userWebApp: Object.freeze({
        intendedUseStatement: STANDARD_USER_WEB_INTENDED_USE_STATEMENT,
      }),
    }),
  }),
  "jp-research": Object.freeze({
    branding: Object.freeze({
      appName: "pDx",
      logoAsset: "pdx_logo.png",
    }),
    labelling: Object.freeze({
      exclusivelyForClinicalInvestigations: true,
      userWebApp: Object.freeze({
        intendedUseStatement: STANDARD_USER_WEB_INTENDED_USE_STATEMENT,
      }),
    }),
  }),
  "us-cds": Object.freeze({
    branding: Object.freeze({
      appName: "purpleDx CDS",
      logoAsset: "purpledx_primary_logo.png",
    }),
    labelling: Object.freeze({
      exclusivelyForClinicalInvestigations: true,
      userWebApp: Object.freeze({
        intendedUseStatement: null,
      }),
    }),
  }),
  "us-510k-denovo": Object.freeze({
    branding: Object.freeze({
      appName: "purpleDx",
      logoAsset: "purpledx_primary_logo.png",
    }),
    labelling: Object.freeze({
      exclusivelyForClinicalInvestigations: true,
      userWebApp: Object.freeze({
        intendedUseStatement: CLINICAL_USER_WEB_INTENDED_USE_STATEMENT,
      }),
    }),
  }),
  "au-research": Object.freeze({
    branding: Object.freeze({
      appName: "pDx",
      logoAsset: "pdx_logo.png",
    }),
    labelling: Object.freeze({
      exclusivelyForClinicalInvestigations: false,
      userWebApp: Object.freeze({
        intendedUseStatement: RESEARCH_USER_WEB_INTENDED_USE_STATEMENT,
      }),
    }),
  }),
  "uk-demo": Object.freeze({
    branding: Object.freeze({
      appName: "pDx Demo",
      logoAsset: "pdx_logo.png",
    }),
    labelling: Object.freeze({
      exclusivelyForClinicalInvestigations: true,
      userWebApp: Object.freeze({
        intendedUseStatement: STANDARD_USER_WEB_INTENDED_USE_STATEMENT,
      }),
    }),
  }),
});

function normalizeProfileId(profileId) {
  if (typeof profileId !== "string") {
    return null;
  }

  const trimmed = profileId.trim().toLowerCase();
  return trimmed || null;
}

function shouldPreferFallbackProfile(productPresentationProfile, fallbackProfileId) {
  const normalizedFallbackId = normalizeProfileId(fallbackProfileId);

  if (!normalizedFallbackId) {
    return false;
  }

  const normalizedLiveProfileId = normalizeProfileId(productPresentationProfile?.id);
  return normalizedLiveProfileId !== normalizedFallbackId;
}

function pickFirstString(...values) {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return null;
}

function pickFirstBoolean(...values) {
  for (const value of values) {
    if (typeof value === "boolean") {
      return value;
    }
  }

  return null;
}

export function resolveFallbackProductPresentationLabelling(profileId) {
  const normalizedProfileId = normalizeProfileId(profileId);
  if (!normalizedProfileId) {
    return null;
  }

  return (
    PRODUCT_PRESENTATION_PROFILE_LABELLING_FALLBACKS[normalizedProfileId] ||
    null
  );
}

export function resolveProductPresentationLabelling(
  productPresentationProfile,
  {
    surfaceKey = "userWebApp",
    fallbackProfileId = null,
    fallbackAppName = "pDx",
    fallbackLogoAsset = "pdx_logo.png",
  } = {},
) {
  const fallbackProfile =
    resolveFallbackProductPresentationLabelling(fallbackProfileId);
  const effectiveLiveProfile = shouldPreferFallbackProfile(
    productPresentationProfile,
    fallbackProfileId,
  )
    ? null
    : productPresentationProfile;
  const liveSurfaceLabelling =
    effectiveLiveProfile?.labelling?.[surfaceKey] || null;
  const fallbackSurfaceLabelling =
    fallbackProfile?.labelling?.[surfaceKey] || null;

  return {
    appName: pickFirstString(
      effectiveLiveProfile?.branding?.appName,
      fallbackProfile?.branding?.appName,
      fallbackAppName,
    ),
    logoAsset: pickFirstString(
      effectiveLiveProfile?.branding?.logoAsset,
      fallbackProfile?.branding?.logoAsset,
      fallbackLogoAsset,
    ),
    intendedUseStatement: pickFirstString(
      liveSurfaceLabelling?.intendedUseStatement,
      fallbackSurfaceLabelling?.intendedUseStatement,
    ),
    exclusivelyForClinicalInvestigations: pickFirstBoolean(
      effectiveLiveProfile?.labelling
        ?.exclusivelyForClinicalInvestigations,
      fallbackProfile?.labelling?.exclusivelyForClinicalInvestigations,
    ),
  };
}
